import { useEffect, useMemo, useRef, useState } from 'react'
import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react'
import { Button } from '~/components/ui/button'
import { cn } from '~/lib/utils'

const WEEKDAYS = ['일', '월', '화', '수', '목', '금', '토']

const pad = (n: number) => String(n).padStart(2, '0')
/** 로컬 기준 'YYYY-MM-DD'. (Date.toISOString은 UTC라 날짜가 밀릴 수 있어 직접 포맷한다.) */
const toISO = (d: Date) =>
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
/** 'YYYY-MM-DD' → Date(로컬). 잘못된 값이면 null. */
function parseISO(value?: string): Date | null {
    if (!value) return null
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value)
    if (!m) return null
    const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
    return Number.isNaN(d.getTime()) ? null : d
}
const sameDay = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()

export interface DatePickerProps {
    /** 'YYYY-MM-DD' */
    value?: string
    onChange(value: string): void
    placeholder?: string
    disabled?: boolean
    ariaLabel?: string
    className?: string
    containerClassName?: string
}

/** 디자인 톤의 단일 날짜 선택기 — 달력 팝오버(연·월 탐색, 오늘/취소/선택). */
export function DatePicker({
    value,
    onChange,
    placeholder = 'YYYY.MM.DD',
    disabled,
    ariaLabel,
    className,
    containerClassName,
}: DatePickerProps) {
    const rootRef = useRef<HTMLDivElement>(null)
    const [open, setOpen] = useState(false)
    // 달력을 fixed로 띄워 모달/overflow 컨테이너(테이블 등)에 잘리지 않게 한다.
    const [dropUp, setDropUp] = useState(false)
    const [menu, setMenu] = useState<{
        left: number
        top?: number
        bottom?: number
    } | null>(null)
    const selectedDate = useMemo(() => parseISO(value), [value])
    const [draft, setDraft] = useState<Date | null>(selectedDate)
    const today = useMemo(() => new Date(), [])
    const [view, setView] = useState(() => selectedDate ?? today)

    useEffect(() => {
        if (!open) return
        setDraft(selectedDate)
        setView(selectedDate ?? today)
        const onPointerDown = (e: PointerEvent) => {
            if (!rootRef.current?.contains(e.target as Node)) setOpen(false)
        }
        document.addEventListener('pointerdown', onPointerDown)
        return () => document.removeEventListener('pointerdown', onPointerDown)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open])

    // 트리거 rect로 달력 fixed 좌표를 계산(아래 공간 부족하면 위로).
    const positionMenu = () => {
        const rect = rootRef.current?.getBoundingClientRect()
        if (!rect) return
        const spaceBelow = window.innerHeight - rect.bottom
        const up = spaceBelow < 340 && rect.top > spaceBelow
        setDropUp(up)
        setMenu({
            left: rect.left,
            ...(up
                ? { bottom: window.innerHeight - rect.top + 4 }
                : { top: rect.bottom + 4 }),
        })
    }

    // 열린 동안 스크롤(capture)·리사이즈에 맞춰 위치를 갱신한다.
    useEffect(() => {
        if (!open) return
        const reposition = () => positionMenu()
        window.addEventListener('resize', reposition)
        window.addEventListener('scroll', reposition, true)
        return () => {
            window.removeEventListener('resize', reposition)
            window.removeEventListener('scroll', reposition, true)
        }
    }, [open])

    const viewYear = view.getFullYear()
    const viewMonth = view.getMonth()
    // 달력 그리드 42칸(이전 달 말일 ~ 다음 달 초).
    const cells = useMemo(() => {
        const first = new Date(viewYear, viewMonth, 1)
        const start = new Date(first)
        start.setDate(1 - first.getDay())
        return Array.from({ length: 42 }, (_, i) => {
            const d = new Date(start)
            d.setDate(start.getDate() + i)
            return d
        })
    }, [viewYear, viewMonth])

    const shiftMonth = (delta: number) =>
        setView(new Date(viewYear, viewMonth + delta, 1))

    const confirm = (d: Date | null) => {
        if (d) onChange(toISO(d))
        setOpen(false)
    }

    const display = selectedDate
        ? `${selectedDate.getFullYear()}.${pad(selectedDate.getMonth() + 1)}.${pad(selectedDate.getDate())}`
        : ''

    return (
        <div
            ref={rootRef}
            className={cn('relative', containerClassName)}
            data-slot='date-picker'>
            <button
                type='button'
                disabled={disabled}
                aria-label={ariaLabel}
                onClick={() => {
                    if (disabled) return
                    if (open) {
                        setOpen(false)
                        return
                    }
                    positionMenu()
                    setOpen(true)
                }}
                className={cn(
                    'border-input bg-card text-foreground focus-visible:ring-ring flex h-10 w-full items-center justify-between gap-2 rounded-md border px-3 text-left text-sm transition-colors outline-none focus-visible:ring-2 disabled:cursor-not-allowed disabled:opacity-50',
                    open ? 'border-primary ring-ring ring-2' : '',
                    className
                )}>
                <span
                    className={
                        display ? 'text-foreground' : 'text-muted-foreground'
                    }>
                    {display || placeholder}
                </span>
                <Calendar className='text-muted-foreground size-4 shrink-0' />
            </button>

            {open && menu ? (
                <div
                    style={{
                        position: 'fixed',
                        left: menu.left,
                        ...(dropUp
                            ? { bottom: menu.bottom }
                            : { top: menu.top }),
                    }}
                    className='border-border bg-card z-50 w-[17rem] rounded-md border p-3 shadow-lg'>
                    {/* 연·월 탐색 */}
                    <div className='mb-2 flex items-center justify-between'>
                        <button
                            type='button'
                            onClick={() => shiftMonth(-1)}
                            className='hover:bg-accent text-foreground rounded p-1'
                            aria-label='이전 달'>
                            <ChevronLeft className='size-4' />
                        </button>
                        <span className='text-foreground text-sm font-semibold'>
                            {viewYear}년 {viewMonth + 1}월
                        </span>
                        <button
                            type='button'
                            onClick={() => shiftMonth(1)}
                            className='hover:bg-accent text-foreground rounded p-1'
                            aria-label='다음 달'>
                            <ChevronRight className='size-4' />
                        </button>
                    </div>

                    {/* 요일 */}
                    <div className='text-muted-foreground mb-1 grid grid-cols-7 text-center text-xs'>
                        {WEEKDAYS.map((w) => (
                            <span key={w} className='py-1'>
                                {w}
                            </span>
                        ))}
                    </div>

                    {/* 날짜 그리드 */}
                    <div className='grid grid-cols-7 gap-0.5'>
                        {cells.map((d, i) => {
                            const inMonth = d.getMonth() === viewMonth
                            const isToday = sameDay(d, today)
                            const isSelected =
                                draft != null && sameDay(d, draft)
                            return (
                                <button
                                    key={i}
                                    type='button'
                                    onClick={() => setDraft(d)}
                                    className={cn(
                                        'relative mx-auto flex size-8 items-center justify-center rounded-full text-sm transition-colors',
                                        isSelected
                                            ? 'bg-primary text-primary-foreground font-semibold'
                                            : inMonth
                                              ? 'text-foreground hover:bg-accent'
                                              : 'text-muted-foreground/50 hover:bg-accent/60'
                                    )}>
                                    {d.getDate()}
                                    {isToday && !isSelected ? (
                                        <span className='bg-primary absolute bottom-1 size-1 rounded-full' />
                                    ) : null}
                                </button>
                            )
                        })}
                    </div>

                    {/* 푸터 */}
                    <div className='border-border mt-2 flex items-center justify-between border-t pt-2'>
                        <button
                            type='button'
                            onClick={() => {
                                setView(today)
                                setDraft(today)
                            }}
                            className='text-muted-foreground hover:text-foreground text-xs'>
                            오늘
                        </button>
                        <div className='flex gap-1.5'>
                            <Button
                                variant='outline'
                                size='sm'
                                onClick={() => setOpen(false)}>
                                취소
                            </Button>
                            <Button
                                size='sm'
                                disabled={!draft}
                                onClick={() => confirm(draft)}>
                                선택
                            </Button>
                        </div>
                    </div>
                </div>
            ) : null}
        </div>
    )
}

export interface DateTimePickerProps extends Omit<
    DatePickerProps,
    'value' | 'onChange'
> {
    /** 'YYYY-MM-DDTHH:mm' */
    value?: string
    onChange(value: string): void
}

/** 날짜(달력) + 시각(시:분) 선택 — 측정 시각 등 datetime 입력용. */
export function DateTimePicker({
    value,
    onChange,
    disabled,
    ariaLabel,
    containerClassName,
}: DateTimePickerProps) {
    const [datePart, timePart] = (value ?? '').split('T')
    const time = timePart?.slice(0, 5) || '00:00'

    return (
        <div className={cn('flex items-center gap-2', containerClassName)}>
            <DatePicker
                value={datePart || undefined}
                onChange={(d) => onChange(`${d}T${time}`)}
                disabled={disabled}
                ariaLabel={ariaLabel}
                containerClassName='min-w-0 flex-1'
            />
            <input
                type='time'
                value={time}
                disabled={disabled}
                onChange={(e) =>
                    onChange(
                        `${datePart || toISO(new Date())}T${e.target.value}`
                    )
                }
                className='border-input bg-card text-foreground focus-visible:ring-ring h-10 shrink-0 rounded-md border px-2.5 text-sm outline-none focus-visible:ring-2 disabled:opacity-50'
            />
        </div>
    )
}
